import React, { useState } from "react";
import { Block, flattenIOPS, IOPS } from "../../models/block";
import { Card, CardContent, Typography, Collapse, IconButton, Box } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { getCompactNumberStr } from "../../utils/iops-utils";

interface BlockInfoDisplayProps {
  block: Block;
  currentInputIOPS?: IOPS;
}

// Deterministic color for each op-content key
const IOPS_COLORS: { [key: string]: string } = {
  "read-static": "#1976d2",
  "read-dynamic": "#43a047",
  "write-dynamic": "#fbc02d",
  "write-static": "#e53935",
};

function getIOPSColor(key: string) {
  if (IOPS_COLORS[key]) return IOPS_COLORS[key];
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue},70%,80%)`;
}



const IOPSDisplay = ({ iops }: { iops: IOPS | undefined }) => {
  if (!iops) return null;
  const flat = flattenIOPS(iops);
  return (
    <div style={{ marginLeft: 8 }}>
      {Object.entries(flat)
        .filter(([key]) => key !== "total")
        .map(([key, val]) => (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                borderRadius: 2,
                background: getIOPSColor(key),
                marginRight: 6,
              }}
            />
            <span style={{ fontWeight: 500 }}>{key}:</span>{" "}
            <span>{getCompactNumberStr(val as number)}</span>
          </div>
        ))}
      <div>
        <span style={{ fontWeight: 500 }}>Total:</span>{" "}
        <span>{getCompactNumberStr(flat.total)}</span>
      </div>
    </div>
  );
};

const CollapsibleSection = ({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <IconButton size="small" sx={{ p: 0, mr: 0.5 }}>
          {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
        </IconButton>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Collapse in={open}>
        <Box sx={{ pl: 3 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

export const BlockInfoDisplay: React.FC<BlockInfoDisplayProps> = ({ block, currentInputIOPS }) => {
  return (
    <Card
      style={{
        marginLeft: 12,
        minWidth: 180,
        background: "#f7f7fa",
        boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
        fontSize: 12,
      }}
      elevation={2}
    >
      <CardContent style={{ padding: 10 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
          Block Info
        </Typography>

        <CollapsibleSection title="Current Input IOPS">
            <IOPSDisplay iops={currentInputIOPS} />
        </CollapsibleSection>
        <CollapsibleSection title="Input Capacity" defaultOpen>
            <IOPSDisplay iops={block.inputCapacity} />
        </CollapsibleSection>

        <CollapsibleSection title="Processing Capacity">
            <IOPSDisplay iops={block.processingCapacity} />
        </CollapsibleSection>

        

        {block.dynamicProperties && Object.keys(block.dynamicProperties).length > 0 && (
          <Box mt={1}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Dynamic Properties
            </Typography>
            <Box sx={{ pl: 1 }}>
              {Object.entries(block.dynamicProperties).map(([key, value]) => (
                <div key={key}>
                  <span style={{ fontWeight: 500 }}>{key}:</span> <br/> <span>{JSON.stringify(value)}</span>
                </div>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
